import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private isNative = Capacitor.isNativePlatform();
  private initialized = false;

  constructor() { 
    this.initializeStorage();
  }

  private async initializeStorage(): Promise<void> {
    if (this.isNative && !this.initialized) {
      try {
        await Preferences.configure({
          group: 'PadelPointApp'
        });
        this.initialized = true;
        console.log('Preferences configured for native platform');
        
        await this.migrateFromLocalStorage();
      } catch (error) {
        console.error('Error configuring Preferences:', error);
      }
    }
  }

  private async migrateFromLocalStorage(): Promise<void> {
    try {
      const migrated = await Preferences.get({ key: '_migrated' });
      if (!migrated.value) {
        console.log('Migrating from localStorage to Preferences...');
        await Preferences.migrate();
        await Preferences.set({ key: '_migrated', value: 'true' });
        console.log('Migration complete');
      }
    } catch (error) {
      console.error('Error during migration:', error);
    }
  }

  async setItem(key: string, value: any): Promise<void> {
    await this.ensureInitialized();
    
    try {
      const stringValue = JSON.stringify(value);
      
      if (this.isNative) {
        await Preferences.set({
          key: key,
          value: stringValue
        });
        console.log(`Stored ${key} in Preferences`);
      } else {
        localStorage.setItem(key, stringValue);
      }
    } catch (error) {
      console.error(`Error setting item ${key}:`, error);
      throw error;
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (this.isNative && !this.initialized) {
      await this.initializeStorage();
    }
  }

  async getItem(key: string): Promise<any> {
    await this.ensureInitialized();
    
    try {
      if (this.isNative) {
        const { value } = await Preferences.get({ key });
        console.log(`Retrieved ${key} from Preferences:`, value ? 'found' : 'null');
        return value ? this.parseValue(value) : null;
      } else {
        const localValue = localStorage.getItem(key);
        if (localValue !== null) {
          return this.parseValue(localValue);
        }

        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue !== null) {
          return this.parseValue(sessionValue);
        }

        return null;
      }
    } catch (error) {
      console.error(`Error getting item ${key}:`, error);
      return null;
    }
  }

  async exists(key: string): Promise<boolean> {
    await this.ensureInitialized();
    
    try {
      if (this.isNative) {
        const { value } = await Preferences.get({ key });
        return value !== null;
      } else {
        return (
          localStorage.getItem(key) !== null ||
          sessionStorage.getItem(key) !== null
        );
      }
    } catch (error) {
      console.error(`Error checking existence of ${key}:`, error);
      return false;
    }
  }

  async remove(key: string): Promise<void> {
    await this.ensureInitialized();
    
    try {
      if (this.isNative) {
        await Preferences.remove({ key });
        console.log(`Removed ${key} from Preferences`);
      } else {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error removing item ${key}:`, error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    await this.ensureInitialized();
    
    try {
      if (this.isNative) {
        await Preferences.clear();
        console.log('Cleared all Preferences');
      } else {
        localStorage.clear();
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
      throw error;
    }
  }

  private parseValue(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  async getAllKeys(): Promise<string[]> {
    await this.ensureInitialized();
    
    try {
      if (this.isNative) {
        const { keys } = await Preferences.keys();
        console.log('All stored keys:', keys);
        return keys;
      } else {
        return Object.keys(localStorage);
      }
    } catch (error) {
      console.error('Error getting keys:', error);
      return [];
    }
  }

  async diagnose(): Promise<void> {
    console.log('=== Storage Diagnostics ===');
    console.log('Platform:', this.isNative ? 'Native' : 'Web');
    console.log('Initialized:', this.initialized);
    
    const keys = await this.getAllKeys();
    console.log('Total keys stored:', keys.length);
    console.log('Keys:', keys);
    
    for (const key of keys) {
      const value = await this.getItem(key);
      console.log(`${key}:`, typeof value, value ? 'exists' : 'null');
    }
    console.log('=========================');
  }
}
